import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ImageUploadService } from '../../../services/image-upload.service';

export interface ImageUploadRequestV2 {
  tagName: string;
  currentImageSrc: string;
  section: string; // The section this edit belongs to (capabilities, about-us, etc.)
}

@Component({
  selector: 'app-inline-image-uploader-v2',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './inline-image-uploader-v2.component.html',
  styleUrls: ['./inline-image-uploader-v2.component.scss']
})
export class InlineImageUploaderV2Component implements OnInit, OnDestroy, OnChanges {
  @Input() uploadRequest: ImageUploadRequestV2 | null = null;
  @Input() sectionTheme: string = 'default'; // Section-specific styling theme
  @Input() currentSupplierData: any = {};
  @Input() companyGuid: string = ''; // Company GUID for S3 upload
  
  @Output() imageUpdated = new EventEmitter<{ tagName: string; newImageSrc: string; imageBlob: Blob }>();
  @Output() uploadCancelled = new EventEmitter<void>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('imagePreview') imagePreview!: ElementRef<HTMLImageElement>;

  // Image state
  tagName: string = '';
  originalImageSrc: string = '';
  newImageSrc: string = '';
  selectedFile: File | null = null;
  isUploading: boolean = false;
  uploadProgress: number = 0;
  imageDimensions: { width: number; height: number } | null = null;
  previewImageLoaded: boolean = false;
  
  // Resize state tracking
  isResizing: boolean = false;
  originalImageDimensions: { width: number; height: number } | null = null;
  resizedImageDimensions: { width: number; height: number } | null = null;
  
  // Drag and drop state
  isDragOver: boolean = false;
  
  // Prevent unnecessary re-initialization
  private lastProcessedRequest: ImageUploadRequestV2 | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private imageUploadService: ImageUploadService
  ) {}

  ngOnInit(): void {
    console.log('V2 Image Uploader: ngOnInit called');
    console.log('V2 Image Uploader: uploadRequest:', this.uploadRequest);
    console.log('V2 Image Uploader: sectionTheme:', this.sectionTheme);
    
    if (this.uploadRequest) {
      this.initializeUploader(this.uploadRequest);
    } else {
      console.warn('V2 Image Uploader: No uploadRequest provided');
    }
  }

  ngOnChanges(): void {
    if (this.uploadRequest && this.uploadRequest !== this.lastProcessedRequest) {
      console.log('V2 Image Uploader: Processing new upload request:', this.uploadRequest);
      this.lastProcessedRequest = this.uploadRequest;
      this.initializeUploader(this.uploadRequest);
    }
  }

  ngOnDestroy(): void {
    // Clean up blob URLs to prevent memory leaks
    // if (this.newImageSrc && this.newImageSrc.startsWith('blob:')) {
    //   URL.revokeObjectURL(this.newImageSrc);
    // }
  }

  private initializeUploader(request: ImageUploadRequestV2): void {
    console.log(`V2 Image Uploader: Initializing for section ${request.section}, tag ${request.tagName}`);
    
    this.originalImageSrc = request.currentImageSrc || '';
    this.newImageSrc = request.currentImageSrc || '';
    this.selectedFile = null;
    this.isUploading = false;
    this.uploadProgress = 0;
    this.imageDimensions = null;
    this.previewImageLoaded = false;
    this.isDragOver = false;
    this.tagName = request.tagName;

    // If there's an existing image, try to load its dimensions
    if (this.originalImageSrc) {
      this.loadImageDimensions(this.originalImageSrc);
    }
  }

  // File Selection Methods
  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFileSelection(input.files[0]);
    }
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  // Drag and Drop Methods
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileSelection(files[0]);
    }
  }

  // File Processing
  private async handleFileSelection(file: File): Promise<void> {
    console.log('V2 Image Uploader: File selected:', file.name, file.type, file.size);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.');
      return;
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert('Image file is too large. Please select a file smaller than 10MB.');
      return;
    }

    try {
      // Check if we have placeholder dimensions to resize to
      if (this.imageDimensions && this.tagName !== 'IMG_COMPANY_LOGO') {
        console.log('V2 Image Uploader: Resizing to fit within dimensions:', this.imageDimensions);
        
        // Set loading state
        this.isResizing = true;
        this.cdr.detectChanges();
        
        // Resize image to fit within placeholder dimensions
        const resizedBlob = await this.resizeImageToFitPlaceholder(file);
        
        // Create new file from resized blob
        this.selectedFile = new File(
          [resizedBlob], 
          file.name, 
          { 
            type: 'image/jpeg',
            lastModified: Date.now()
          }
        );
        
        console.log('V2 Image Uploader: Image resized successfully');
        
      } else {
        console.log('V2 Image Uploader: No placeholder dimensions, using original file');
        this.selectedFile = file;
        // Clear resize tracking for original file
        this.originalImageDimensions = null;
        this.resizedImageDimensions = null;
      }
      
      // Create preview with the processed file
      this.createImagePreview(this.selectedFile);
      
    } catch (error) {
      console.error('V2 Image Uploader: Error processing image:', error);
      alert('Error processing the selected image. Please try again.');
    } finally {
      this.isResizing = false;
      this.cdr.detectChanges();
    }
  }

  private createImagePreview(file: File): void {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const imageUrl = e.target?.result as string;
      this.newImageSrc = imageUrl;
      this.previewImageLoaded = false;
      
      // Load image dimensions
      this.loadImageDimensions(imageUrl);
      
      this.cdr.detectChanges();
    };
    
    reader.onerror = () => {
      console.error('V2 Image Uploader: Error reading file');
      alert('Error reading the selected file. Please try again.');
    };
    
    reader.readAsDataURL(file);
  }

  private loadImageDimensions(imageSrc: string): void {
    const img = new Image();
    
    img.onload = () => {
      this.imageDimensions = {
        width: img.width,
        height: img.height
      };
      this.previewImageLoaded = true;
      this.cdr.detectChanges();
    };
    
    img.onerror = () => {
      console.warn('V2 Image Uploader: Could not load image dimensions');
      this.imageDimensions = null;
      this.previewImageLoaded = true;
      this.cdr.detectChanges();
    };
    
    img.src = imageSrc;
  }

  /**
   * Resize image to exact placeholder dimensions using smart fill approach
   * Images are scaled and centered to fill the entire placeholder space with minimal cropping
   */
  private resizeImageToFitPlaceholder(file: File): Promise<Blob> {
    return new Promise((resolve, reject) => {
      // Use existing image dimensions as maximum bounds
      if (!this.imageDimensions) {
        reject(new Error('No placeholder dimensions available'));
        return;
      }
      
      const maxWidth = this.imageDimensions.width;
      const maxHeight = this.imageDimensions.height;
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      
      const img = new Image();
      
      img.onload = () => {
        try {
          // Store original dimensions
          this.originalImageDimensions = {
            width: img.width,
            height: img.height
          };
          
          // Smart fill approach: Always use exact placeholder dimensions
          // Calculate scaling to cover entire area with minimal cropping
          const scaleX = maxWidth / img.width;
          const scaleY = maxHeight / img.height;
          const scale = Math.max(scaleX, scaleY); // Use larger scale to cover entire area
          
          // Calculate scaled dimensions
          const scaledWidth = img.width * scale;
          const scaledHeight = img.height * scale;
          
          // Calculate offset to center the scaled image
          const offsetX = (maxWidth - scaledWidth) / 2;
          const offsetY = (maxHeight - scaledHeight) / 2;
          
          // Store resized dimensions (always exact placeholder dimensions)
          this.resizedImageDimensions = {
            width: maxWidth,
            height: maxHeight
          };
          
          // Set canvas size to exact placeholder dimensions
          canvas.width = maxWidth;
          canvas.height = maxHeight;
          
          // Configure canvas for high quality
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // Draw image scaled and centered to fill entire canvas
          ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);
          
          // Convert to blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                console.log('V2 Image Uploader: Image resized to exact placeholder dimensions', {
                  placeholderDimensions: `${maxWidth} × ${maxHeight}`,
                  originalDimensions: `${this.originalImageDimensions?.width} × ${this.originalImageDimensions?.height}`,
                  scaleUsed: scale.toFixed(2),
                  finalDimensions: `${this.resizedImageDimensions?.width} × ${this.resizedImageDimensions?.height}`,
                  fileSize: `${(blob.size / 1024).toFixed(2)} KB`
                });
                resolve(blob);
              } else {
                reject(new Error('Canvas to blob conversion failed'));
              }
            },
            'image/jpeg',
            0.9 // 90% quality
          );
          
        } catch (error) {
          console.error('V2 Image Uploader: Error during image resize:', error);
          reject(error);
        }
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image for resizing'));
      };
      
      img.src = URL.createObjectURL(file);
    });
  }

  // Image Preview Event
  onImageLoad(): void {
    this.previewImageLoaded = true;
  }

  onImageError(): void {
    console.warn('V2 Image Uploader: Image failed to load');
    this.previewImageLoaded = true;
  }

  // Actions
  async saveImage(): Promise<void> {
    if (this.uploadRequest && this.selectedFile && this.companyGuid) {
      console.log(`V2 Image Uploader: Uploading image to S3 for tag ${this.uploadRequest.tagName}`);
      
      this.isUploading = true;
      this.uploadProgress = 0;
      this.cdr.detectChanges();

      try {
        // Upload to S3 via API
        const response = await firstValueFrom(this.imageUploadService.uploadImage(
          this.companyGuid,
          this.selectedFile,
          this.uploadRequest.tagName
        ));

        if (!response) {
          throw new Error('No response received from upload service');
        }

        console.log('V2 Image Uploader: S3 upload successful:', response);
        
        // Emit the S3 URL instead of blob URL
        this.imageUpdated.emit({
          tagName: this.uploadRequest.tagName,
          newImageSrc: response.s3_url,
          imageBlob: this.selectedFile // Keep for compatibility
        });

      } catch (error) {
        console.error('V2 Image Uploader: S3 upload failed:', error);
        
        // Fallback to blob URL if S3 upload fails
        // this.imageUpdated.emit({
        //   tagName: this.uploadRequest.tagName,
        //   newImageSrc: this.newImageSrc,
        //   imageBlob: this.selectedFile
        // });
      } finally {
        this.isUploading = false;
        this.uploadProgress = 0;
        this.cdr.detectChanges();
      }
    } else if (this.uploadRequest && this.selectedFile) {
      // Fallback when no company GUID is available
      console.warn('V2 Image Uploader: No company GUID available, using blob URL fallback');
      this.imageUpdated.emit({
        tagName: this.uploadRequest.tagName,
        newImageSrc: this.newImageSrc,
        imageBlob: this.selectedFile
      });
    }
  }

  removeImage(): void {
    this.selectedFile = null;
    this.newImageSrc = '';
    this.imageDimensions = null;
    this.previewImageLoaded = false;
    
    // Reset file input
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  restoreOriginal(): void {
    this.newImageSrc = this.originalImageSrc;
    this.selectedFile = null;
    this.previewImageLoaded = false;
    
    // Reset file input
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
    
    if (this.originalImageSrc) {
      this.loadImageDimensions(this.originalImageSrc);
    }
  }

  cancelUpload(): void {
    console.log('V2 Image Uploader: Upload cancelled');
    this.uploadCancelled.emit();
  }

  // Helper Methods
  getSectionDisplayName(): string {
    const sectionNames: { [key: string]: string } = {
      'capabilities': 'Capabilities',
      'about-us': 'About Us', 
      'contact': 'Contact',
      'template': 'Home'
    };
    return sectionNames[this.sectionTheme] || 'Content';
  }

  getImageLabel(): string {
    return `Update ${this.getSectionDisplayName()} Image`;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  hasNewImage(): boolean {
    return !!this.selectedFile;
  }

  hasImageChanged(): boolean {
    return this.newImageSrc !== this.originalImageSrc;
  }
} 